/**
 * AccessFlow — ADA Accessibility Widget
 * Vanilla JS — no frameworks, CSP-safe, <50KB budget
 *
 * All DOM created via document.createElement (no innerHTML).
 * State persisted in localStorage.
 */
(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────
  var STORAGE_KEY = 'accessflow_state';
  var POSITION_KEY = 'accessflow_position';
  var ROOT_ID = 'accessflow-root';

  // ── Default State ──────────────────────────────────────────
  var DEFAULT_STATE = {
    fontSize: 0,        // 0=off, 1-3=levels
    highContrast: false,
    monochrome: false,
    dyslexiaFont: false,
    textSpacing: 0,     // 0=off, 1-3=levels
    largeCursor: false,
    linkHighlight: false,
    readingGuide: false,
    hideImages: false,
    pauseAnimations: false,
    focusIndicators: false,
    activeProfile: null
  };

  // ── Profile Definitions ────────────────────────────────────
  var PROFILES = {
    visionImpaired: {
      label: 'Vision Impaired',
      icon: '\u{1F441}',
      state: { fontSize: 2, highContrast: true, focusIndicators: true, linkHighlight: true }
    },
    adhdFriendly: {
      label: 'ADHD Friendly',
      icon: '\u{1F3AF}',
      state: { readingGuide: true, pauseAnimations: true, hideImages: true }
    },
    seizureSafe: {
      label: 'Seizure Safe',
      icon: '\u{1F6E1}',
      state: { pauseAnimations: true, monochrome: true }
    },
    dyslexiaFriendly: {
      label: 'Dyslexia Friendly',
      icon: '\u{1F4D6}',
      state: { dyslexiaFont: true, textSpacing: 2, fontSize: 1 }
    },
    keyboardNav: {
      label: 'Keyboard Nav',
      icon: '\u{2328}',
      state: { focusIndicators: true }
    }
  };

  // ── SVG Icons ──────────────────────────────────────────────
  var ICON_ACCESSIBILITY = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="4" r="2"/><path d="M10 22V15.5L6.5 13 8 8.5l4-1 4 1 1.5 4.5L14 15.5V22h-4z"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>';
  var ICON_PLAY = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><polygon points="6,3 20,12 6,21" fill="currentColor"/></svg>';
  var ICON_PAUSE = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect x="5" y="3" width="4" height="18" fill="currentColor"/><rect x="15" y="3" width="4" height="18" fill="currentColor"/></svg>';
  var ICON_STOP = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor"/></svg>';

  // ── State Management ───────────────────────────────────────
  var state = loadState();

  function loadState() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        var parsed = JSON.parse(saved);
        // Merge with defaults to handle new fields
        var merged = {};
        for (var key in DEFAULT_STATE) {
          merged[key] = parsed.hasOwnProperty(key) ? parsed[key] : DEFAULT_STATE[key];
        }
        return merged;
      }
    } catch (e) { /* ignore */ }
    return JSON.parse(JSON.stringify(DEFAULT_STATE));
  }

  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* ignore */ }
  }

  function resetState() {
    if (ttsSupported()) ttsStop();
    state = JSON.parse(JSON.stringify(DEFAULT_STATE));
    saveState();
    applyAll();
    renderPanel();
  }

  // ── DOM Helpers ────────────────────────────────────────────
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var key in attrs) {
        if (key === 'className') node.className = attrs[key];
        else if (key === 'textContent') node.textContent = attrs[key];
        else if (key.startsWith('on')) node.addEventListener(key.slice(2).toLowerCase(), attrs[key]);
        else if (key === 'htmlContent') node.innerHTML = attrs[key];
        else node.setAttribute(key, attrs[key]);
      }
    }
    if (children) {
      for (var i = 0; i < children.length; i++) {
        if (typeof children[i] === 'string') node.appendChild(document.createTextNode(children[i]));
        else if (children[i]) node.appendChild(children[i]);
      }
    }
    return node;
  }

  // ── Apply CSS Classes to <html> ────────────────────────────
  var htmlEl = document.documentElement;

  function applyClass(className, active) {
    if (active) htmlEl.classList.add(className);
    else htmlEl.classList.remove(className);
  }

  function applyAll() {
    // Font size
    applyClass('af-font-1', state.fontSize === 1);
    applyClass('af-font-2', state.fontSize === 2);
    applyClass('af-font-3', state.fontSize === 3);

    // Toggles
    applyClass('af-high-contrast', state.highContrast);
    applyClass('af-monochrome', state.monochrome);
    applyClass('af-dyslexia-font', state.dyslexiaFont);
    applyClass('af-large-cursor', state.largeCursor);
    applyClass('af-link-highlight', state.linkHighlight);
    applyClass('af-reading-guide-on', state.readingGuide);
    applyClass('af-hide-images', state.hideImages);
    applyClass('af-pause-animations', state.pauseAnimations);
    applyClass('af-focus-indicators', state.focusIndicators);

    // Text spacing
    applyClass('af-spacing-1', state.textSpacing === 1);
    applyClass('af-spacing-2', state.textSpacing === 2);
    applyClass('af-spacing-3', state.textSpacing === 3);
  }

  // ── Reading Guide ──────────────────────────────────────────
  var readingGuideEl = null;

  function initReadingGuide() {
    if (readingGuideEl) return;
    readingGuideEl = el('div', { className: 'af-reading-guide' });
    document.body.appendChild(readingGuideEl);
    document.addEventListener('mousemove', function (e) {
      if (state.readingGuide) {
        readingGuideEl.style.top = (e.clientY - 6) + 'px';
      }
    });
  }

  // ── Skip to Content ────────────────────────────────────────
  var skipLinkEl = null;

  function initSkipLink() {
    if (skipLinkEl) return;
    var main = document.querySelector('main, [role="main"], #main, #MainContent');
    if (!main) return;
    if (!main.id) main.id = 'af-main-content';
    skipLinkEl = el('a', {
      className: 'af-skip-link',
      href: '#' + main.id,
      textContent: 'Skip to content'
    });
    document.body.insertBefore(skipLinkEl, document.body.firstChild);
  }

  // ── Text-to-Speech (Read Page) ─────────────────────────────
  var tts = {
    synth: window.speechSynthesis || null,
    utterance: null,
    isReading: false,
    isPaused: false,
    rate: 1.0,
    mode: 'auto',        // 'auto' = read full page, 'click' = click to read
    clickActive: false,   // click-to-read listener is active
    highlightEl: null,
    elements: [],
    index: 0
  };

  function ttsSupported() {
    return !!(tts.synth && typeof SpeechSynthesisUtterance !== 'undefined');
  }

  function ttsCollectElements() {
    var main = document.querySelector('main, [role="main"], #main, #MainContent');
    var scope = main || document.body;
    var candidates = scope.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, figcaption, dt, dd');
    var result = [];
    for (var i = 0; i < candidates.length; i++) {
      var node = candidates[i];
      if (node.closest('#accessflow-root, .af-panel, .af-trigger')) continue;
      var text = (node.textContent || '').trim();
      if (text.length > 1) result.push(node);
    }
    return result;
  }

  // ── Auto-read mode ──
  function ttsStart() {
    if (!ttsSupported()) return;
    ttsClickDeactivate(); // turn off click mode if active
    tts.synth.cancel();
    tts.elements = ttsCollectElements();
    if (tts.elements.length === 0) return;
    tts.index = 0;
    tts.isReading = true;
    tts.isPaused = false;
    ttsReadCurrent();
    updateTtsControls();
  }

  function ttsReadCurrent() {
    if (tts.index >= tts.elements.length) {
      ttsStop();
      return;
    }
    var node = tts.elements[tts.index];
    ttsSpeak(node, function () {
      tts.index++;
      if (tts.isReading && !tts.isPaused) {
        ttsReadCurrent();
      }
    });
  }

  // ── Shared speak function (used by both modes) ──
  function ttsSpeak(node, onDone) {
    var text = (node.textContent || '').trim();
    if (!text) { if (onDone) onDone(); return; }

    // Also read alt text for images
    if (node.tagName === 'IMG' && node.alt) {
      text = 'Image: ' + node.alt;
    }

    ttsRemoveHighlight();
    node.classList.add('af-tts-highlight');
    tts.highlightEl = node;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });

    tts.utterance = new SpeechSynthesisUtterance(text);
    tts.utterance.rate = tts.rate;
    tts.utterance.onend = function () {
      ttsRemoveHighlight();
      if (onDone) onDone();
    };
    tts.utterance.onerror = function () {
      ttsRemoveHighlight();
      if (onDone) onDone();
    };
    tts.synth.speak(tts.utterance);
  }

  function ttsPause() {
    if (!tts.isReading) return;
    tts.isPaused = true;
    tts.synth.pause();
    updateTtsControls();
  }

  function ttsResume() {
    if (!tts.isReading) return;
    tts.isPaused = false;
    tts.synth.resume();
    updateTtsControls();
  }

  function ttsStop() {
    tts.isReading = false;
    tts.isPaused = false;
    tts.index = 0;
    tts.synth.cancel();
    ttsClickDeactivate();
    ttsRemoveHighlight();
    updateTtsControls();
  }

  // ── Click-to-read mode ──
  function ttsClickHandler(e) {
    // Find the closest readable element from click target
    var target = e.target;
    if (target.closest('#accessflow-root, .af-panel, .af-trigger')) return;

    // Walk up to find a text-bearing element
    var readable = target.closest('p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, figcaption, dt, dd, a, span, label, button, img');
    if (!readable) readable = target;

    var text = (readable.textContent || '').trim();
    // For images, read alt text
    if (readable.tagName === 'IMG') {
      text = readable.alt ? 'Image: ' + readable.alt : '';
    }
    if (!text || text.length < 2) return;

    e.preventDefault();
    e.stopPropagation();

    tts.synth.cancel();
    ttsSpeak(readable, null);
  }

  function ttsClickActivate() {
    if (tts.clickActive) return;
    tts.clickActive = true;
    // Stop any auto-reading
    tts.isReading = false;
    tts.isPaused = false;
    tts.synth.cancel();
    ttsRemoveHighlight();
    // Add click listener and cursor style
    document.addEventListener('click', ttsClickHandler, true);
    document.documentElement.classList.add('af-click-to-read');
    updateTtsControls();
  }

  function ttsClickDeactivate() {
    if (!tts.clickActive) return;
    tts.clickActive = false;
    tts.synth.cancel();
    document.removeEventListener('click', ttsClickHandler, true);
    document.documentElement.classList.remove('af-click-to-read');
    ttsRemoveHighlight();
  }

  function ttsRemoveHighlight() {
    if (tts.highlightEl) {
      tts.highlightEl.classList.remove('af-tts-highlight');
      tts.highlightEl = null;
    }
    var strays = document.querySelectorAll('.af-tts-highlight');
    for (var i = 0; i < strays.length; i++) {
      strays[i].classList.remove('af-tts-highlight');
    }
  }

  function ttsSetRate(newRate) {
    tts.rate = newRate;
    if (tts.isReading) {
      tts.synth.cancel();
      ttsReadCurrent();
    }
  }

  // Reference to TTS control elements for live updates
  var ttsControlsRef = {};

  function updateTtsControls() {
    if (!ttsControlsRef.autoBtn) return;
    var playing = tts.isReading && !tts.isPaused;
    var paused = tts.isReading && tts.isPaused;

    // Auto button
    if (playing) {
      ttsControlsRef.autoBtn.innerHTML = ICON_PAUSE;
      ttsControlsRef.autoBtn.setAttribute('aria-label', 'Pause reading');
    } else if (paused) {
      ttsControlsRef.autoBtn.innerHTML = ICON_PLAY;
      ttsControlsRef.autoBtn.setAttribute('aria-label', 'Resume reading');
    } else {
      ttsControlsRef.autoBtn.innerHTML = ICON_PLAY;
      ttsControlsRef.autoBtn.setAttribute('aria-label', 'Read entire page');
    }

    // Click-to-read button active state
    ttsControlsRef.clickBtn.setAttribute('data-active', tts.clickActive.toString());
    ttsControlsRef.clickBtn.setAttribute('aria-pressed', tts.clickActive.toString());

    // Stop button visibility
    ttsControlsRef.stopBtn.style.display = (tts.isReading || tts.clickActive) ? '' : 'none';

    // Status text
    if (tts.clickActive) {
      ttsControlsRef.status.textContent = 'Click anything to hear it';
    } else if (playing) {
      ttsControlsRef.status.textContent = 'Reading...';
    } else if (paused) {
      ttsControlsRef.status.textContent = 'Paused';
    } else {
      ttsControlsRef.status.textContent = '';
    }

    // Sync stop label
    if (ttsControlsRef.stopLabel) {
      ttsControlsRef.stopLabel.style.display = (tts.isReading || tts.clickActive) ? '' : 'none';
    }
  }

  // ── Build UI ───────────────────────────────────────────────
  var root, trigger, panel, backdrop, panelBody;
  var isOpen = false;

  function init() {
    root = document.getElementById(ROOT_ID);
    if (!root) return;

    var position = root.getAttribute('data-position') || 'bottom-right';
    var accent = root.getAttribute('data-accent') || '#2563EB';
    var iconSize = parseInt(root.getAttribute('data-icon-size') || '56', 10);

    // Apply accent color
    document.documentElement.style.setProperty('--af-accent', accent);
    document.documentElement.style.setProperty('--af-trigger-size', iconSize + 'px');

    // Create trigger button
    trigger = el('button', {
      className: 'af-trigger',
      'data-position': position,
      'aria-label': 'Accessibility Options — drag to reposition',
      'aria-expanded': 'false',
      'aria-controls': 'af-panel',
      htmlContent: ICON_ACCESSIBILITY,
      onClick: function (e) {
        // Don't open panel if user just finished dragging
        if (drag.moved) return;
        togglePanel();
      }
    });

    // Create backdrop
    backdrop = el('div', {
      className: 'af-backdrop',
      'data-open': 'false',
      onClick: closePanel
    });

    // Create panel
    panel = el('div', {
      className: 'af-panel',
      id: 'af-panel',
      'data-position': position,
      'data-open': 'false',
      role: 'dialog',
      'aria-label': 'Accessibility Settings',
      'aria-modal': 'true'
    });

    // Panel header
    var header = el('div', { className: 'af-panel-header' }, [
      el('h2', { className: 'af-panel-title', textContent: 'Accessibility' }),
      el('button', {
        className: 'af-panel-close',
        'aria-label': 'Close accessibility panel',
        htmlContent: ICON_CLOSE,
        onClick: closePanel
      })
    ]);

    // Panel body
    panelBody = el('div', { className: 'af-panel-body' });

    // Panel footer
    var footer = el('div', { className: 'af-panel-footer' }, [
      el('div', { className: 'af-branding', textContent: 'Powered by AccessFlow' })
    ]);

    panel.appendChild(header);
    panel.appendChild(panelBody);
    panel.appendChild(footer);

    root.appendChild(trigger);
    root.appendChild(backdrop);
    root.appendChild(panel);

    // Initialize features
    initReadingGuide();
    initSkipLink();
    initDrag();

    // Apply saved state
    applyAll();

    // Render panel contents
    renderPanel();

    // Keyboard handling
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closePanel();
    });
  }

  function togglePanel() {
    if (isOpen) closePanel();
    else openPanel();
  }

  function openPanel() {
    isOpen = true;
    panel.setAttribute('data-open', 'true');
    backdrop.setAttribute('data-open', 'true');
    trigger.setAttribute('aria-expanded', 'true');
    // Focus trap: focus the close button
    var closeBtn = panel.querySelector('.af-panel-close');
    if (closeBtn) closeBtn.focus();
  }

  function closePanel() {
    isOpen = false;
    panel.setAttribute('data-open', 'false');
    backdrop.setAttribute('data-open', 'false');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.focus();
  }

  // ── Draggable Launcher ────────────────────────────────────
  var drag = {
    active: false,
    startX: 0,
    startY: 0,
    triggerX: 0,
    triggerY: 0,
    moved: false,
    longPressTimer: null,
    dragThreshold: 5
  };

  function loadSavedPosition() {
    try {
      var saved = localStorage.getItem(POSITION_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) { /* ignore */ }
    return null;
  }

  function savePosition(x, y) {
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify({ x: x, y: y }));
    } catch (e) { /* ignore */ }
  }

  function applyCustomPosition(x, y) {
    var size = parseInt(getComputedStyle(trigger).width, 10) || 56;
    // Clamp to viewport
    x = Math.max(4, Math.min(x, window.innerWidth - size - 4));
    y = Math.max(4, Math.min(y, window.innerHeight - size - 4));

    // Snap to nearest edge (left or right)
    var snapMargin = 20;
    if (x < window.innerWidth / 2) {
      x = snapMargin;
    } else {
      x = window.innerWidth - size - snapMargin;
    }

    trigger.setAttribute('data-custom-pos', 'true');
    trigger.style.left = x + 'px';
    trigger.style.top = y + 'px';

    // Update panel position based on which side
    var panelSide = (x < window.innerWidth / 2) ? 'left' : 'right';
    var verticalPos = y < window.innerHeight / 3 ? 'top' : 'bottom';
    var newPosition = verticalPos + '-' + panelSide;
    panel.setAttribute('data-position', newPosition);

    return { x: x, y: y };
  }

  function initDrag() {
    if (!trigger) return;

    // Restore saved position
    var saved = loadSavedPosition();
    if (saved) {
      applyCustomPosition(saved.x, saved.y);
    }

    // Mouse events
    trigger.addEventListener('mousedown', onDragStart);
    document.addEventListener('mousemove', onDragMove);
    document.addEventListener('mouseup', onDragEnd);

    // Touch events
    trigger.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);

    // Recalculate on window resize
    window.addEventListener('resize', function () {
      if (trigger.getAttribute('data-custom-pos') === 'true') {
        var rect = trigger.getBoundingClientRect();
        var snapped = applyCustomPosition(rect.left, rect.top);
        savePosition(snapped.x, snapped.y);
      }
    });
  }

  function onDragStart(e) {
    if (isOpen) return;
    drag.startX = e.clientX;
    drag.startY = e.clientY;
    var rect = trigger.getBoundingClientRect();
    drag.triggerX = rect.left;
    drag.triggerY = rect.top;
    drag.moved = false;
    drag.active = true;

    // Show drag hint after 300ms hold
    drag.longPressTimer = setTimeout(function () {
      if (drag.active && !drag.moved) {
        trigger.classList.add('af-drag-hint');
      }
    }, 300);
  }

  function onDragMove(e) {
    if (!drag.active) return;
    var dx = e.clientX - drag.startX;
    var dy = e.clientY - drag.startY;

    if (!drag.moved && (Math.abs(dx) > drag.dragThreshold || Math.abs(dy) > drag.dragThreshold)) {
      drag.moved = true;
      trigger.classList.add('af-dragging');
      trigger.classList.remove('af-drag-hint');
      clearTimeout(drag.longPressTimer);
    }

    if (drag.moved) {
      e.preventDefault();
      var newX = drag.triggerX + dx;
      var newY = drag.triggerY + dy;
      // Free-move during drag (no snap yet)
      trigger.setAttribute('data-custom-pos', 'true');
      trigger.style.left = newX + 'px';
      trigger.style.top = newY + 'px';
    }
  }

  function onDragEnd(e) {
    if (!drag.active) return;
    clearTimeout(drag.longPressTimer);
    trigger.classList.remove('af-dragging', 'af-drag-hint');

    if (drag.moved) {
      // Snap to edge and save
      var rect = trigger.getBoundingClientRect();
      var snapped = applyCustomPosition(rect.left, rect.top);
      savePosition(snapped.x, snapped.y);
      e.preventDefault();
      e.stopPropagation();
    }

    drag.active = false;
    drag.moved = false;
  }

  // Touch equivalents
  function onTouchStart(e) {
    if (isOpen) return;
    var touch = e.touches[0];
    drag.startX = touch.clientX;
    drag.startY = touch.clientY;
    var rect = trigger.getBoundingClientRect();
    drag.triggerX = rect.left;
    drag.triggerY = rect.top;
    drag.moved = false;
    drag.active = true;

    drag.longPressTimer = setTimeout(function () {
      if (drag.active && !drag.moved) {
        trigger.classList.add('af-drag-hint');
      }
    }, 300);
  }

  function onTouchMove(e) {
    if (!drag.active) return;
    var touch = e.touches[0];
    var dx = touch.clientX - drag.startX;
    var dy = touch.clientY - drag.startY;

    if (!drag.moved && (Math.abs(dx) > drag.dragThreshold || Math.abs(dy) > drag.dragThreshold)) {
      drag.moved = true;
      trigger.classList.add('af-dragging');
      trigger.classList.remove('af-drag-hint');
      clearTimeout(drag.longPressTimer);
    }

    if (drag.moved) {
      e.preventDefault();
      trigger.setAttribute('data-custom-pos', 'true');
      trigger.style.left = (drag.triggerX + dx) + 'px';
      trigger.style.top = (drag.triggerY + dy) + 'px';
    }
  }

  function onTouchEnd(e) {
    if (!drag.active) return;
    clearTimeout(drag.longPressTimer);
    trigger.classList.remove('af-dragging', 'af-drag-hint');

    if (drag.moved) {
      var rect = trigger.getBoundingClientRect();
      var snapped = applyCustomPosition(rect.left, rect.top);
      savePosition(snapped.x, snapped.y);
      e.preventDefault();
    }

    drag.active = false;
    drag.moved = false;
  }

  // ── Render Panel Contents ──────────────────────────────────
  function renderPanel() {
    // Clear panel body
    while (panelBody.firstChild) panelBody.removeChild(panelBody.firstChild);

    // Profiles section
    panelBody.appendChild(buildProfilesSection());

    // Visual adjustments section
    panelBody.appendChild(buildSection('Visual Adjustments', [
      buildLevelControl('Font Size', 'Increase text size', 'fontSize', 3),
      buildToggle('High Contrast', 'Enhance color contrast', 'highContrast'),
      buildToggle('Monochrome', 'Remove colors', 'monochrome'),
      buildToggle('Dyslexia Font', 'OpenDyslexic typeface', 'dyslexiaFont'),
      buildLevelControl('Text Spacing', 'Increase spacing', 'textSpacing', 3)
    ]));

    // Navigation section
    panelBody.appendChild(buildSection('Navigation', [
      buildToggle('Focus Indicators', 'Enhanced focus outlines', 'focusIndicators'),
      buildToggle('Large Cursor', 'Enlarge mouse cursor', 'largeCursor'),
      buildToggle('Highlight Links', 'Make links visible', 'linkHighlight')
    ]));

    // Read Page section (text-to-speech)
    if (ttsSupported()) {
      panelBody.appendChild(buildTtsSection());
    }

    // Content section
    panelBody.appendChild(buildSection('Content', [
      buildToggle('Reading Guide', 'Line follows cursor', 'readingGuide'),
      buildToggle('Hide Images', 'Remove images', 'hideImages'),
      buildToggle('Pause Animations', 'Stop motion', 'pauseAnimations')
    ]));

    // Reset button
    var resetBtn = el('button', {
      className: 'af-reset',
      textContent: 'Reset All Settings',
      onClick: resetState
    });
    panelBody.appendChild(resetBtn);
  }

  function buildSection(title, items) {
    var section = el('div', { className: 'af-section' }, [
      el('div', { className: 'af-section-title', textContent: title })
    ]);
    for (var i = 0; i < items.length; i++) {
      section.appendChild(items[i]);
    }
    return section;
  }

  // ── Build Toggle ───────────────────────────────────────────
  function buildToggle(name, desc, key) {
    var id = 'af-toggle-' + key;
    var input = el('input', {
      type: 'checkbox',
      id: id,
      'aria-label': name
    });
    input.checked = state[key];
    input.addEventListener('change', function () {
      state[key] = input.checked;
      state.activeProfile = null;
      saveState();
      applyAll();
      updateProfileButtons();
    });

    var toggle = el('label', { className: 'af-toggle', 'for': id }, [
      input,
      el('span', { className: 'af-toggle-track' })
    ]);

    return el('div', { className: 'af-feature' }, [
      el('div', { className: 'af-feature-info' }, [
        el('div', { className: 'af-feature-name', textContent: name }),
        el('div', { className: 'af-feature-desc', textContent: desc })
      ]),
      toggle
    ]);
  }

  // ── Build Level Control ────────────────────────────────────
  function buildLevelControl(name, desc, key, maxLevel) {
    var levelWrap = el('div', { className: 'af-level' });

    for (var i = 0; i <= maxLevel; i++) {
      (function (level) {
        var btn = el('button', {
          className: 'af-level-btn',
          'data-active': (state[key] === level).toString(),
          textContent: level === 0 ? 'Off' : level.toString(),
          'aria-label': name + ' level ' + level,
          onClick: function () {
            state[key] = level;
            state.activeProfile = null;
            saveState();
            applyAll();
            // Update button states
            var btns = levelWrap.querySelectorAll('.af-level-btn');
            for (var j = 0; j < btns.length; j++) {
              btns[j].setAttribute('data-active', (j === level).toString());
            }
            updateProfileButtons();
          }
        });
        levelWrap.appendChild(btn);
      })(i);
    }

    return el('div', { className: 'af-feature' }, [
      el('div', { className: 'af-feature-info' }, [
        el('div', { className: 'af-feature-name', textContent: name }),
        el('div', { className: 'af-feature-desc', textContent: desc })
      ]),
      levelWrap
    ]);
  }

  // ── Build TTS Section ──────────────────────────────────────
  var ICON_CURSOR_CLICK = '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 2l8 20 2.5-7.5L22 12 4 2z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>';

  function buildTtsSection() {
    var section = el('div', { className: 'af-section' }, [
      el('div', { className: 'af-section-title', textContent: 'Read Aloud' })
    ]);

    // Mode buttons row: Auto Read | Click to Read | Stop
    var controls = el('div', { className: 'af-tts-controls' });

    // Auto-read button (play/pause)
    var autoBtn = el('button', {
      className: 'af-tts-btn af-tts-play',
      'aria-label': 'Read entire page',
      title: 'Read entire page',
      htmlContent: ICON_PLAY,
      onClick: function () {
        if (!tts.isReading) {
          ttsStart();
        } else if (tts.isPaused) {
          ttsResume();
        } else {
          ttsPause();
        }
      }
    });

    // Click-to-read button
    var clickBtn = el('button', {
      className: 'af-tts-btn af-tts-click',
      'aria-label': 'Click to read — click any element to hear it',
      title: 'Click to read',
      'data-active': tts.clickActive.toString(),
      'aria-pressed': tts.clickActive.toString(),
      htmlContent: ICON_CURSOR_CLICK,
      onClick: function () {
        if (tts.clickActive) {
          ttsClickDeactivate();
          updateTtsControls();
        } else {
          ttsClickActivate();
        }
      }
    });

    // Stop button
    var stopBtn = el('button', {
      className: 'af-tts-btn af-tts-stop',
      'aria-label': 'Stop reading',
      title: 'Stop',
      htmlContent: ICON_STOP,
      onClick: ttsStop
    });
    stopBtn.style.display = (tts.isReading || tts.clickActive) ? '' : 'none';

    // Status text
    var status = el('span', { className: 'af-tts-status' });

    controls.appendChild(autoBtn);
    controls.appendChild(clickBtn);
    controls.appendChild(stopBtn);
    controls.appendChild(status);

    // Store references for live updates
    ttsControlsRef.autoBtn = autoBtn;
    ttsControlsRef.clickBtn = clickBtn;
    ttsControlsRef.stopBtn = stopBtn;
    ttsControlsRef.status = status;

    section.appendChild(controls);

    // Labels under buttons
    var labels = el('div', { className: 'af-tts-labels' }, [
      el('span', { className: 'af-tts-label', textContent: 'Auto' }),
      el('span', { className: 'af-tts-label', textContent: 'Click' }),
      el('span', { className: 'af-tts-label af-tts-label-stop', textContent: 'Stop' })
    ]);
    // Sync stop label visibility
    labels.children[2].style.display = (tts.isReading || tts.clickActive) ? '' : 'none';
    ttsControlsRef.stopLabel = labels.children[2];
    section.appendChild(labels);

    // Speed control
    var speedWrap = el('div', { className: 'af-feature' }, [
      el('div', { className: 'af-feature-info' }, [
        el('div', { className: 'af-feature-name', textContent: 'Speed' }),
        el('div', { className: 'af-feature-desc', textContent: 'Adjust reading speed' })
      ])
    ]);

    var speedLevels = el('div', { className: 'af-level' });
    var speeds = [
      { label: '0.5x', value: 0.5 },
      { label: '0.75x', value: 0.75 },
      { label: '1x', value: 1.0 },
      { label: '1.5x', value: 1.5 },
      { label: '2x', value: 2.0 }
    ];

    for (var i = 0; i < speeds.length; i++) {
      (function (spd) {
        var btn = el('button', {
          className: 'af-level-btn',
          'data-active': (tts.rate === spd.value).toString(),
          textContent: spd.label,
          'aria-label': 'Reading speed ' + spd.label,
          onClick: function () {
            tts.rate = spd.value;
            ttsSetRate(spd.value);
            var btns = speedLevels.querySelectorAll('.af-level-btn');
            for (var j = 0; j < btns.length; j++) {
              btns[j].setAttribute('data-active', (j === i).toString());
            }
          }
        });
        speedLevels.appendChild(btn);
      })(speeds[i]);
    }

    speedWrap.appendChild(speedLevels);
    section.appendChild(speedWrap);

    updateTtsControls();

    return section;
  }

  // ── Build Profiles ─────────────────────────────────────────
  var profileButtons = {};

  function buildProfilesSection() {
    var section = el('div', { className: 'af-section' }, [
      el('div', { className: 'af-section-title', textContent: 'Quick Profiles' })
    ]);
    var grid = el('div', { className: 'af-profiles' });
    profileButtons = {};

    for (var key in PROFILES) {
      (function (profileKey) {
        var profile = PROFILES[profileKey];
        var btn = el('button', {
          className: 'af-profile-btn',
          'data-active': (state.activeProfile === profileKey).toString(),
          'aria-label': profile.label + ' profile',
          'aria-pressed': (state.activeProfile === profileKey).toString(),
          onClick: function () {
            applyProfile(profileKey);
          }
        }, [
          el('span', { className: 'af-profile-icon', textContent: profile.icon }),
          el('span', { className: 'af-profile-label', textContent: profile.label })
        ]);
        profileButtons[profileKey] = btn;
        grid.appendChild(btn);
      })(key);
    }

    section.appendChild(grid);
    return section;
  }

  function applyProfile(profileKey) {
    if (state.activeProfile === profileKey) {
      // Toggle off — reset to defaults
      resetState();
      return;
    }

    // Reset to defaults first
    for (var k in DEFAULT_STATE) state[k] = DEFAULT_STATE[k];

    // Apply profile overrides
    var profileState = PROFILES[profileKey].state;
    for (var key in profileState) {
      state[key] = profileState[key];
    }
    state.activeProfile = profileKey;

    saveState();
    applyAll();
    renderPanel();
  }

  function updateProfileButtons() {
    for (var key in profileButtons) {
      var active = state.activeProfile === key;
      profileButtons[key].setAttribute('data-active', active.toString());
      profileButtons[key].setAttribute('aria-pressed', active.toString());
    }
  }

  // ── Initialize ─────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
